import { useRef, useState, useEffect, useLayoutEffect } from "react";
import styled from "@emotion/styled";
import { surface, border as borderToken, shadow, borderRadius, spacing, zIndex, layout } from "@atlas-int/tokens";
import SettingsRadioGroup from "./SettingsRadioGroup";
import { DefaultSettingsButton } from "./styles";
import type { SelectBoxItem } from "../dropdownPrimitive/types";

// ─── Styled components ──────────────────────────────────────────────────────

const DropdownPanel = styled.div<{ $alignRight: boolean }>`
  position: absolute;
  top: calc(100% + ${spacing[1]});
  ${({ $alignRight }) => ($alignRight ? "right: 0;" : "left: 0;")}
  z-index: ${zIndex.settingsDropdown};
  min-width: 200px;
  max-height: 70vh;
  overflow-y: auto;
  background: ${surface.raised};
  border-radius: ${borderRadius.base};
  box-shadow: ${shadow.sm};
  border: 1px solid ${borderToken.control};
  padding: ${spacing[2]} 0;
`;

const DropdownFooter = styled.div`
  padding: ${spacing[4]} ${spacing[6]};
  border-top: 1px solid ${borderToken.subtle};
  display: flex;
  justify-content: center;
`;

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SettingsGroup {
  label: string;
  /** Tooltip explanation shown on hover via an info icon next to the label */
  tooltip?: string;
  items: SelectBoxItem[];
  selectedValue: string | number | undefined;
  onChange: (value: string | number) => void;
  disabled?: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface CompactSettingsDropdownProps {
  onClose: () => void;
  /** Settings groups to render as radio groups */
  groups: SettingsGroup[];
  /** Additional content rendered after radio groups (e.g. sliders) */
  extraContent?: React.ReactNode;
  /** Callback for the "Default Settings" button */
  onResetDefaults?: () => void;
  /**
   * Left-edge boundary in viewport pixels. The panel will be shifted so its
   * left edge stays at or to the right of this value, useful to avoid being
   * hidden behind a fixed sidebar.
   */
  leftBoundary?: number;
}

const CompactSettingsDropdown = ({
  onClose,
  groups,
  extraContent,
  onResetDefaults,
  leftBoundary = 0,
}: CompactSettingsDropdownProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [alignRight, setAlignRight] = useState(true);

  // Reposition the panel: clear any prior inline overrides, constrain the
  // width to the visible area between the boundary and the viewport edge,
  // pick alignment, then clamp edges so the panel can't hide behind the
  // sidebar or run off the right of the viewport. Re-runs on resize.
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const margin = 8;

    const reposition = () => {
      // Reset overrides so measurements reflect the natural styled position
      el.style.left = "";
      el.style.right = "";
      el.style.maxWidth = "";

      const available = window.innerWidth - leftBoundary - margin * 2;
      if (available > 0) el.style.maxWidth = `${available}px`;

      // Pick the alignment that fits best given the boundary
      const initial = el.getBoundingClientRect();
      const overflowsRight = initial.right > window.innerWidth - margin;
      const overflowsLeft = initial.left < leftBoundary + margin;
      if (overflowsRight && !overflowsLeft) setAlignRight(true);
      else if (overflowsLeft && !overflowsRight) setAlignRight(false);

      // Clamp whichever edge is still out of bounds after alignment
      const rect = el.getBoundingClientRect();
      if (rect.left < leftBoundary + margin) {
        el.style.left = `${leftBoundary + margin - rect.left}px`;
        el.style.right = "auto";
      } else if (rect.right > window.innerWidth - margin) {
        el.style.right = `${rect.right - window.innerWidth + margin}px`;
        el.style.left = "auto";
      }
    };

    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [alignRight, leftBoundary]);

  // Close when clicking outside (but not the settings toggle button itself)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking the toggle button -- let its onClick handle it
      if (target.closest("[data-settings-toggle]")) return;
      if (panelRef.current && !panelRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <DropdownPanel ref={panelRef} $alignRight={alignRight}>
      {groups.map((group) => (
        <SettingsRadioGroup
          key={group.label}
          label={group.label}
          tooltip={group.tooltip}
          items={group.items}
          selectedValue={group.selectedValue}
          onChange={group.onChange}
        />
      ))}
      {extraContent}
      {onResetDefaults && (
        <DropdownFooter>
          <DefaultSettingsButton variant="default" size="lg" onClick={onResetDefaults}>
            Default Settings
          </DefaultSettingsButton>
        </DropdownFooter>
      )}
    </DropdownPanel>
  );
};

export default CompactSettingsDropdown;
